import { useState } from 'react';
import { DownloadIcon, ShieldIcon } from 'lucide-react';
import { ADMIN_EXPORT_MAX_RANGE_DAYS } from '@qaytim/shared';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/date-picker';
import { Field } from '@/components/field';
import { PageHeader } from '@/components/page-header';
import { ErrorNotice, SuccessNotice } from '@/components/states';
import { adminDownload } from '@/lib/admin-api';
import { strings } from '@/i18n/strings';

/**
 * CSV eksport (§5 P4.2b).
 *
 * Oraliqni ekran TEKSHIRMAYDI — server tekshiradi va rad javobini kod bilan qaytaradi. Klientda
 * ikkinchi nusxa qoida bo'lardi, va u chegara o'zgarganda serverdan ajralib qolardi (§0: server
 * yagona haqiqat manbai).
 */
export function ExportPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<unknown>(null);

  const download = async (event: React.FormEvent) => {
    event.preventDefault();
    setState('busy');
    setError(null);
    try {
      await adminDownload(
        `/admin/export/receipts.csv?from=${from}&to=${to}`,
        `qaytim-receipts-${from}_${to}.csv`,
      );
      setState('done');
    } catch (cause) {
      setError(cause);
      setState('idle');
    }
  };

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={strings.exportData.title} description={strings.exportData.subtitle} />

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle>{strings.exportData.heading}</CardTitle>
          <CardDescription>
            {strings.exportData.rangeHint.replace('{days}', String(ADMIN_EXPORT_MAX_RANGE_DAYS))}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          <p className="text-muted-foreground bg-muted/50 flex items-start gap-2 rounded-lg border p-3 text-xs">
            <ShieldIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            {strings.exportData.identityNote}
          </p>

          <form className="flex flex-wrap items-end gap-3" onSubmit={download}>
            <Field label={strings.exportData.from} className="w-44">
              <DatePicker
                name="from"
                ariaLabel={strings.exportData.from}
                value={from}
                onChange={setFrom}
              />
            </Field>
            <Field label={strings.exportData.to} className="w-44">
              <DatePicker name="to" ariaLabel={strings.exportData.to} value={to} onChange={setTo} />
            </Field>
            <Button type="submit" loading={state === 'busy'} disabled={from === '' || to === ''}>
              {state === 'busy' ? null : <DownloadIcon aria-hidden="true" />}
              {state === 'busy' ? strings.exportData.downloading : strings.exportData.download}
            </Button>
          </form>

          {error ? <ErrorNotice error={error} /> : null}
          {state === 'done' ? <SuccessNotice>{strings.exportData.done}</SuccessNotice> : null}
        </CardContent>
      </Card>
    </section>
  );
}
